/**
 * RhExportPanel — export Excel des EVP + gestion des clés API paie (LOT D).
 * Conforme mockup_rh_global_agrege (onglet paie/export).
 */
import { useState, useEffect } from 'react';
import { Card, Button, Alert, Badge, Field } from '@/components/ui';
import { Dialog } from '@/components/ui/Dialog';
import { api, ApiException } from '@/lib/api';
import { FileDown, KeyRound, Copy, Trash2 } from 'lucide-react';

const MOIS = ['', 'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
const SERVICES: Array<{ v: string; l: string }> = [
  { v: '', l: 'Tous les services' },
  { v: 'service_technique', l: 'Service technique' },
  { v: 'scolaire_cantine', l: 'Scolaire / cantine' },
  { v: 'grc', l: 'Communication / GRC' },
  { v: 'petite_enfance', l: 'Petite enfance' },
  { v: 'urbanisme', l: 'Urbanisme' },
  { v: 'comptable', l: 'Comptabilité' },
];

export function RhExportPanel() {
  const now = new Date();
  const [annee, setAnnee] = useState(now.getFullYear());
  const [mois, setMois] = useState(now.getMonth() + 1);
  const [service, setService] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [msg, setMsg] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const [keys, setKeys] = useState<Array<{ id: string; label: string; prefixe: string; actif: boolean; createdAt: string; lastUsedAt: string | null }>>([]);
  const [newKeyLabel, setNewKeyLabel] = useState('');
  const [newSecret, setNewSecret] = useState<string | null>(null);
  const [showKeyModal, setShowKeyModal] = useState(false);

  async function chargerKeys() {
    try { setKeys(await api.rh.apiKeys()); } catch { /* ignore */ }
  }
  useEffect(() => { void chargerKeys(); }, []);

  async function exporter() {
    setBusy(true); setError(null); setMsg(null);
    try {
      await api.rh.telechargerExportPaie(annee, mois, service || undefined);
      setMsg('Export téléchargé.');
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
    finally { setBusy(false); }
  }

  async function creerCle() {
    setError(null);
    try {
      const r = await api.rh.creerApiKey(newKeyLabel || 'Clé paie');
      setNewSecret(r.secret); setNewKeyLabel(''); await chargerKeys();
    } catch (e) { setError(e instanceof ApiException ? e.message : 'Erreur'); }
  }
  async function revoquer(id: string) {
    try { await api.rh.revoquerApiKey(id); await chargerKeys(); } catch { /* ignore */ }
  }

  return (
    <div className="space-y-5">
      {error && <Alert variant="error">{error}</Alert>}
      {msg && <Alert variant="success">{msg}</Alert>}

      {/* Export Excel */}
      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-rh-tint text-rh-fg"><FileDown className="h-4 w-4" /></span>
          <div>
            <h2 className="font-display text-base font-semibold text-ink">Export de la paie (Excel)</h2>
            <p className="text-xs text-ink-muted">Éléments variables de paie par agent : heures, heures sup, absences ventilées, compteurs, reports. Feuille récap + un onglet par service.</p>
          </div>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <div className="mb-1 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Mois</div>
            <select value={mois} onChange={(e) => setMois(Number(e.target.value))} className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
              {MOIS.slice(1).map((m, i) => <option key={i + 1} value={i + 1}>{m}</option>)}
            </select>
          </div>
          <div>
            <div className="mb-1 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Année</div>
            <input type="number" value={annee} onChange={(e) => setAnnee(Number(e.target.value))} className="h-10 w-24 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink" />
          </div>
          <div>
            <div className="mb-1 text-2xs uppercase tracking-r-eyebrow text-ink-subtle">Service</div>
            <select value={service} onChange={(e) => setService(e.target.value)} className="h-10 rounded-r-md border border-border bg-surface/60 px-2 text-sm text-ink">
              {SERVICES.map((s) => <option key={s.v} value={s.v}>{s.l}</option>)}
            </select>
          </div>
          <Button variant="rh" loading={busy} onClick={() => void exporter()}><FileDown className="h-4 w-4" /> Télécharger l'Excel</Button>
        </div>
      </Card>

      {/* Clés API */}
      <Card className="p-5">
        <div className="mb-3 flex items-center gap-2">
          <span className="inline-grid h-9 w-9 place-items-center rounded-r-lg bg-rh-tint text-rh-fg"><KeyRound className="h-4 w-4" /></span>
          <div className="flex-1">
            <h2 className="font-display text-base font-semibold text-ink">Clés API paie</h2>
            <p className="text-xs text-ink-muted">Pour que votre logiciel de paie (JVS-Mairistem, Berger-Levrault, Cegid, Sage…) récupère les EVP en JSON automatiquement.</p>
          </div>
          <Button variant="secondary" size="sm" onClick={() => { setShowKeyModal(true); setNewSecret(null); }}>＋ Générer une clé</Button>
        </div>
        {keys.length === 0 ? (
          <p className="text-sm text-ink-subtle">Aucune clé. Générez-en une pour connecter votre logiciel de paie.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-2xs uppercase tracking-r-eyebrow text-ink-subtle">
                  <th className="py-2 pr-3">Libellé</th>
                  <th className="py-2 pr-3">Préfixe</th>
                  <th className="py-2 pr-3">État</th>
                  <th className="py-2 pr-3">Dernière utilisation</th>
                  <th className="py-2 text-right"></th>
                </tr>
              </thead>
              <tbody>
                {keys.map((k) => (
                  <tr key={k.id} className="border-b border-border/60">
                    <td className="py-2 pr-3 text-ink">{k.label}</td>
                    <td className="py-2 pr-3 font-mono text-xs text-ink-muted">{k.prefixe}…</td>
                    <td className="py-2 pr-3">{k.actif ? <Badge variant="success">Active</Badge> : <Badge variant="neutral">Révoquée</Badge>}</td>
                    <td className="py-2 pr-3 text-ink-muted">{k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleDateString('fr-FR') : '—'}</td>
                    <td className="py-2 text-right">{k.actif && <button type="button" onClick={() => void revoquer(k.id)} className="text-xs text-ink-subtle hover:text-danger"><Trash2 className="inline h-3.5 w-3.5" /> Révoquer</button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div className="mt-4 rounded-r-lg border border-border bg-surface/40 p-3 text-xs text-ink-muted">
          <strong className="text-ink">Endpoint :</strong> <code className="font-mono">GET /api/paie/evp?annee=AAAA&mois=MM&service=…</code><br />
          En-tête : <code className="font-mono">X-API-Key: votre_clé</code> — renvoie les mêmes EVP que l'Excel, en JSON.
        </div>
      </Card>

      {/* Modale génération de clé */}
      <Dialog open={showKeyModal} onClose={() => setShowKeyModal(false)} title="Générer une clé API"
        footer={newSecret ? <Button variant="rh" onClick={() => setShowKeyModal(false)}>Fermer</Button> : <>
          <Button variant="ghost" onClick={() => setShowKeyModal(false)}>Annuler</Button>
          <Button variant="rh" onClick={() => void creerCle()}>Générer</Button>
        </>}>
        {newSecret ? (
          <div className="space-y-3">
            <Alert variant="warning"><strong>Copiez cette clé maintenant.</strong> Elle ne sera plus jamais affichée.</Alert>
            <div className="flex items-center gap-2 rounded-r-lg border border-border bg-surface/60 p-3">
              <code className="flex-1 break-all font-mono text-xs text-ink">{newSecret}</code>
              <button type="button" onClick={() => void navigator.clipboard?.writeText(newSecret)} className="text-ink-muted hover:text-ink"><Copy className="h-4 w-4" /></button>
            </div>
          </div>
        ) : (
          <Field label="Libellé de la clé" htmlFor="key-label">
            <input id="key-label" type="text" value={newKeyLabel} onChange={(e) => setNewKeyLabel(e.target.value)}
              placeholder="Ex. JVS-Mairistem production"
              className="h-11 w-full rounded-r-lg border border-border bg-surface/60 px-3.5 text-sm text-ink" />
          </Field>
        )}
      </Dialog>
    </div>
  );
}
